import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import type { TroubleshootingFunnelApi } from '@openx/types/troubleshootingFunnel.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { DealHealthScore, type DealHealthScoreValue } from '../../../components/DealHealthScore/DealHealthScore.tsx';
import { useDealContext } from '../../../utils/context/DealContext.tsx';
import { getHealthScoreStateFromScore } from '../../../utils/healthScoreUtils.ts';
import { MetricsOverviewItem } from './MetricsOverviewItem.tsx';

const HealthScoreOverviewItem = ({
  isLast24h,
  onOpen,
  onReload,
  showCurrentState,
  value,
}: {
  isLast24h: boolean;
  onOpen?: () => void;
  onReload?: () => void;
  showCurrentState: boolean;
  value: DealHealthScoreValue;
}) => {
  const { t } = useTranslation();

  return (
    <Box data-test="funnel-metrics-health-score" sx={{ width: 310 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, whiteSpace: 'nowrap' }}>
        <Typography variant="body2Bold" color={palette.text.secondary}>
          {t('Health Score')}
        </Typography>
      </Box>
      <Box sx={{ width: '100%' }}>
        <DealHealthScore
          maxWidth={100}
          onOpen={onOpen}
          onReload={onReload}
          showCurrentState={showCurrentState}
          value={value}
        />
      </Box>
      <Typography
        data-test="funnel-metrics-health-score-description"
        variant="body2"
        color={palette.text.secondary}
        sx={{ mt: 2.625 }}
      >
        {isLast24h
          ? t(
              "Refreshed hourly deal's operational health score, based on its full funnel performance (current and historical), sustained trends, and the performance of deals with a similar setup."
            )
          : t(
              "Averaged across the selected range deal's operational health score, based on its full funnel performance (current and historical), sustained trends, and the performance of deals with a similar setup."
            )}
      </Typography>
    </Box>
  );
};

export const MetricsOverview = ({
  data,
  dealId,
  isLast24h,
}: {
  data: TroubleshootingFunnelApi;
  dealId: string;
  isLast24h: boolean;
}) => {
  const { t } = useTranslation();
  const { isPub } = usePermissionContext();
  const { useDealHealthScoreRootCauseDetails } = useDealContext();

  const healthScore = data.health_score ?? null;
  const healthState = getHealthScoreStateFromScore(healthScore);
  const showCurrentState = !isLast24h;

  const { getDealRootCauseDetails, loadDealRootCauseDetails, reloadDealRootCauseDetails } =
    useDealHealthScoreRootCauseDetails();
  const rootCauseDetails = getDealRootCauseDetails(dealId);

  const handleOpenRootCauseDetails = useCallback(() => {
    loadDealRootCauseDetails(dealId);
  }, [dealId, loadDealRootCauseDetails]);

  const handleReloadRootCauseDetails = useCallback(() => {
    reloadDealRootCauseDetails(dealId);
  }, [dealId, reloadDealRootCauseDetails]);

  const healthScoreValue: DealHealthScoreValue = {
    aiAnalysis: rootCauseDetails.aiAnalysis,
    currentScore: rootCauseDetails.currentScore,
    error: rootCauseDetails.error,
    healthState,
    loading: rootCauseDetails.loading,
    score: healthScore,
  };

  const metricsOverviewItems = (
    <>
      <MetricsOverviewItem title={t('Impressions')} value={data.impressions} allowZero />
      <MetricsOverviewItem title={t('Spend')} value={data.spend} prefix="$" allowZero />
      <MetricsOverviewItem title={t('Viewability')} value={data.viewability} suffix="%" />
      <MetricsOverviewItem title={t('CPM')} value={data.cpm} prefix="$" allowZero />
      <MetricsOverviewItem title={t('CTR')} value={data.ctr} suffix="%" />
      <MetricsOverviewItem title={t('VTR')} value={data.vtr} suffix="%" />
      {isPub ? (
        <MetricsOverviewItem title={t('Pub Revenue')} value={data.publisher_revenue ?? null} prefix="$" allowZero />
      ) : null}
    </>
  );

  return (
    <Box
      sx={{
        alignItems: 'start',
        columnGap: 3,
        display: 'flex',
      }}
      data-test="metrics-overview"
    >
      <HealthScoreOverviewItem
        isLast24h={isLast24h}
        onOpen={handleOpenRootCauseDetails}
        onReload={handleReloadRootCauseDetails}
        showCurrentState={showCurrentState}
        value={healthScoreValue}
      />
      <Box
        sx={{
          columnGap: 3,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, max-content)',
          rowGap: 1.5,
        }}
        data-test="metrics-overview-kpi-grid"
      >
        {metricsOverviewItems}
      </Box>
    </Box>
  );
};
